import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PssAuthService, PssPlayerDetails, PssCrewMember, PssCharacterDesign, PssRoomDesign } from '../../../services/pss-auth.service';
import { spriteIdOrEmpty, rarityRank, RARITY_ORDER } from '../pss-display-utils';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-crew-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSpriteComponent],
  templateUrl: './crew-tab.component.html',
  styleUrls: ['./crew-tab.component.css']
})
export class CrewTabComponent {
  @Input() details!: PssPlayerDetails;
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() roomDesigns = new Map<string, PssRoomDesign>();

  @Output() crewSelected = new EventEmitter<PssCrewMember>();

  private static readonly STORAGE_KEY = 'pss_crew_filters';
  private static readonly DONATION_HISTORY_KEY_PREFIX = 'pss_crew_donation_history_';

  crewSearch = '';
  crewAbilityFilter = 'all';
  crewCollectionFilter = 'all';
  crewRarityFilter = 'all';
  crewSortBy: 'name' | 'level' | 'rarity' | 'donationDate' = 'donationDate';
  crewSortAsc = false; // show most recent donations first by default
  showDonated = true;

  // key = characterId, value = donation date ISO string in UTC
  donationHistory = new Map<string, string>();
  // key = characterId, value = { by: borrower name, at: ISO date of borrow message }
  borrowHistory = new Map<string, { by: string; at: string | null }>();
  showLift = true;
  showDeployed = true;

  get crewAbilityOptions(): { value: string; label: string }[] {
    const seen = new Set<string>();
    for (const c of this.details?.crew ?? []) {
      const ability = this.characterDesigns.get(c.designId)?.specialAbilityType?.trim();
      if (ability) seen.add(ability);
    }
    if (seen.size === 0) return [{ value: 'all', label: 'All Abilities' }];
    return [{ value: 'all', label: 'All Abilities' }, ...Array.from(seen).sort().map(v => ({ value: v, label: v }))];
  }

  get crewCollectionOptions(): { value: string; label: string }[] {
    const seen = new Set<string>();
    for (const c of this.details?.crew ?? []) {
      const collection = this.crewCollection(c);
      if (collection) seen.add(collection);
    }
    if (seen.size === 0) return [{ value: 'all', label: 'All Collections' }];
    return [{ value: 'all', label: 'All Collections' }, ...Array.from(seen).sort().map(v => ({ value: v, label: v }))];
  }

  get crewRarityOptions(): { value: string; label: string }[] {
    const seen = new Set<string>();
    for (const c of this.details?.crew ?? []) {
      const rarity = this.crewRarity(c.designId);
      if (rarity) seen.add(rarity);
    }
    if (seen.size === 0) return [{ value: 'all', label: 'All Rarities' }];
    const sorted = RARITY_ORDER.filter(r => seen.has(r));
    const extra = Array.from(seen).filter(r => !RARITY_ORDER.includes(r as typeof RARITY_ORDER[number])).sort();
    return [{ value: 'all', label: 'All Rarities' }, ...sorted.map(r => ({ value: r, label: r })), ...extra.map(r => ({ value: r, label: r }))];
  }

  crewBusy = new Map<string, boolean>();
  crewError = new Map<string, string>();

  // Export crew flow
  exportMode = false;
  selectedCrewIds = new Set<string>();
  exportSuccess = false;

  constructor(private auth: PssAuthService, private featureFlags: FeatureFlagService) {
    this.loadCrewFilters();
    this.loadDonationHistory();
  }

  get basicPlus(): boolean { return this.featureFlags.isBasicPlus(); }
  get restrictedMode(): boolean { return this.featureFlags.isRestrictedMode(); }

  get donationLimit() { return this.details?.hasSubscription ? 6 : 5; }
  get liftCrew()      { return this.filterSortCrew(this.details?.crew.filter(c => !c.deployed && !c.donated) ?? []); }
  get donatedCrew()   { return this.filterSortCrew(this.details?.crew.filter(c => c.donated) ?? []); }
  get deployedCrew()  { return this.filterSortCrew(this.details?.crew.filter(c => c.deployed) ?? []); }

  private filterSortCrew(list: PssCrewMember[]): PssCrewMember[] {
    const q = this.crewSearch.toLowerCase();
    if (q) {
      list = list.filter(c => {
        const crewName = this.crewName(c).toLowerCase();
        const officialName = this.officialCrewName(c).toLowerCase();
        return crewName.includes(q) || officialName.includes(q);
      });
    }

    if (this.crewAbilityFilter !== 'all') {
      list = list.filter(c => {
        const ability = this.characterDesigns.get(c.designId)?.specialAbilityType?.trim() || '';
        return ability.toLowerCase() === this.crewAbilityFilter.toLowerCase();
      });
    }

    if (this.crewCollectionFilter !== 'all') {
      list = list.filter(c => this.crewCollection(c) === this.crewCollectionFilter);
    }

    if (this.crewRarityFilter !== 'all') {
      list = list.filter(c => this.crewRarity(c.designId).toLowerCase() === this.crewRarityFilter.toLowerCase());
    }

    const dir = this.crewSortAsc ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (this.crewSortBy) {
        case 'level':
          return dir * (parseInt(a.level) - parseInt(b.level));
        case 'rarity':
          return dir * (this.rarityOrder(a.designId) - this.rarityOrder(b.designId));
        case 'donationDate': {
          const aDate = this.deploymentTimestamp(this.effectiveDonationDate(a));
          const bDate = this.deploymentTimestamp(this.effectiveDonationDate(b));
          return dir * (aDate - bDate);
        }
        default:
          return dir * this.crewName(a).localeCompare(this.crewName(b));
      }
    });
  }

  private rarityOrder(designId: string): number {
    return rarityRank(this.crewRarity(designId));
  }

  crewName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  officialCrewName(c: PssCrewMember): string {
    return this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  isCrewSelected(c: PssCrewMember): boolean {
    return this.selectedCrewIds.has(c.characterId);
  }

  setCrewSelection(c: PssCrewMember, selected: boolean): void {
    if (selected) this.selectedCrewIds.add(c.characterId);
    else this.selectedCrewIds.delete(c.characterId);
  }

  isSectionAllSelected(section: PssCrewMember[]): boolean {
    return section.length > 0 && section.every(c => this.selectedCrewIds.has(c.characterId));
  }

  isSectionAnySelected(section: PssCrewMember[]): boolean {
    return section.some(c => this.selectedCrewIds.has(c.characterId));
  }

  setSectionSelection(section: PssCrewMember[], selected: boolean): void {
    section.forEach(c => this.setCrewSelection(c, selected));
  }

  toggleExportMode(enable: boolean): void {
    this.exportMode = enable;
    this.selectedCrewIds.clear();
  }

  rowClicked(c: PssCrewMember): void {
    if (this.exportMode) return;
    this.crewSelected.emit(c);
  }

  buildCrewCsv(crew: PssCrewMember[]): string {
    return crew.map(c => this.officialCrewName(c)).join(',');
  }

  async exportSelectedCrew(): Promise<void> {
    const selectedCrew = this.details?.crew.filter(c => this.selectedCrewIds.has(c.characterId)) ?? [];
    if (!selectedCrew.length) return;

    const uniqueCrewById = new Map<string, PssCrewMember>();
    selectedCrew.forEach(c => {
      if (!uniqueCrewById.has(c.characterId)) {
        uniqueCrewById.set(c.characterId, c);
      }
    });

    const uniqueSelectedCrew = Array.from(uniqueCrewById.values());
    const csv = this.buildCrewCsv(uniqueSelectedCrew);

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(csv);
      } catch {
        this.copyTextFallback(csv);
      }
    } else {
      this.copyTextFallback(csv);
    }

    this.exportSuccess = true;
    this.selectedCrewIds.clear();
    this.exportMode = false;

    setTimeout(() => {
      this.exportSuccess = false;
    }, 2000);
  }

  private copyTextFallback(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch {
      // ignore
    }
    document.body.removeChild(textarea);
  }

  crewCollection(c: PssCrewMember): string {
    const design = this.characterDesigns.get(c.designId) as (PssCharacterDesign & { collection?: string }) | undefined;
    return design?.collection || 'Unknown';
  }

  setCrewSearch(value: string): void {
    this.crewSearch = value;
    this.saveCrewFilters();
  }

  setCrewAbilityFilter(value: string): void {
    this.crewAbilityFilter = value;
    this.saveCrewFilters();
  }

  setCrewCollectionFilter(value: string): void {
    this.crewCollectionFilter = value;
    this.saveCrewFilters();
  }

  setCrewRarityFilter(value: string): void {
    this.crewRarityFilter = value;
    this.saveCrewFilters();
  }

  setCrewSort(col: 'name' | 'level' | 'rarity' | 'donationDate'): void {
    if (this.crewSortBy === col) { this.crewSortAsc = !this.crewSortAsc; }
    else { this.crewSortBy = col; this.crewSortAsc = true; }
    this.saveCrewFilters();
  }

  crewRarity(designId: string): string {
    return this.characterDesigns.get(designId)?.rarity ?? '';
  }

  crewSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.characterDesigns.get(designId)?.profileSpriteId);
  }

  roomName(roomDesignId: string): string {
    return this.roomDesigns.get(roomDesignId)?.name ?? '';
  }

  private isRealDeploymentDate(date: string): boolean {
    if (!date) return false;
    return !date.startsWith('2000-01-01') && !date.startsWith('1900-01-01');
  }

  private deploymentTimestamp(date: string): number {
    if (!this.isRealDeploymentDate(date)) return 0;
    const d = new Date(date.endsWith('Z') ? date : date + 'Z');
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }

  private donationHistoryKey(): string {
    const userId = this.auth.currentSession?.userId || 'unknown';
    return `${CrewTabComponent.DONATION_HISTORY_KEY_PREFIX}${userId}`;
  }

  private loadDonationHistory(): void {
    const userId = this.auth.currentSession?.userId;
    if (!userId || !this.auth.getCrewDonationHistory) return;

    this.auth.getCrewDonationHistory(userId).subscribe(items => {
      this.donationHistory = new Map(items.map(i => [i.characterId, i.deploymentDate]));
      this.borrowHistory = new Map(
        items
          .filter(i => i.lastBorrowedBy)
          .map(i => [i.characterId, { by: i.lastBorrowedBy as string, at: i.lastBorrowedAt ?? null }])
      );
      this.backfillDonationNames(items);
    });
  }

  // Donation rows created before migration 0027 (or by the polling-task extend
  // path) have NULL character_name / user_name, which prevents both the
  // fleet-chat poller and the GET-side resolver from matching borrow lines.
  // For every currently-donated crew member whose row is missing either name,
  // re-POST with the names filled in. The server uses COALESCE so this only
  // adds the names — deployment_date is preserved by passing the existing
  // value back. Idempotent: subsequent loads see populated rows and skip.
  private donationNamesBackfilled = false;
  private backfillDonationNames(items: import('../../../services/pss-auth.service').PssDonationHistoryItem[]): void {
    if (this.donationNamesBackfilled) return;
    const userId = this.auth.currentSession?.userId;
    const userName = this.auth.currentSession?.name || null;
    if (!userId || !userName) return;

    const byId = new Map(items.map(i => [i.characterId, i]));
    const donated = (this.details?.crew ?? []).filter(c => c.donated);
    let scheduled = 0;
    for (const c of donated) {
      const row = byId.get(c.characterId);
      const customName = (c.customName || '').trim() || null;
      const needsName        = !row?.characterName;
      const needsCustomName  = !!customName && !row?.characterCustomName;
      const needsUser        = !row?.userName;
      if (!needsName && !needsUser && !needsCustomName) continue;

      const characterName = this.characterDesigns.get(c.designId)?.name || null;
      if (!characterName) continue; // designs may not be loaded yet — try next time

      // Prefer the stored deployment_date so we don't reset the donation clock.
      // Fall back to the live crew's deploymentDate, then now() as a last resort.
      const deploymentDate =
        row?.deploymentDate ||
        c.deploymentDate ||
        new Date().toISOString().replace('Z', '');

      this.auth.setCrewDonationHistory(userId, c.characterId, deploymentDate, characterName, userName, customName).subscribe();
      scheduled++;
    }
    // Only flip the guard if designs were loaded — otherwise we want a retry
    // on the next tab load after designs arrive.
    if (this.characterDesigns.size > 0) this.donationNamesBackfilled = true;
    if (scheduled > 0) {
      // After the names land, re-fetch so borrowHistory picks up any borrowers
      // the GET-side resolver can now match. Small delay to let the writes
      // commit; the GET resolver does the LIKE scan itself.
      setTimeout(() => {
        this.auth.getCrewDonationHistory(userId).subscribe(refreshed => {
          this.borrowHistory = new Map(
            refreshed
              .filter(i => i.lastBorrowedBy)
              .map(i => [i.characterId, { by: i.lastBorrowedBy as string, at: i.lastBorrowedAt ?? null }])
          );
        });
      }, 1500);
    }
  }

  lastBorrowedBy(c: PssCrewMember): string {
    return this.borrowHistory.get(c.characterId)?.by ?? '';
  }

  lastBorrowedAtLabel(c: PssCrewMember): string {
    const at = this.borrowHistory.get(c.characterId)?.at;
    if (!at) return '';
    const iso = at.endsWith('Z') ? at : at + 'Z';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return iso.replace('T', ' ').replace('Z', '').replace('.000', '');
  }

  private saveDonationHistory(c: PssCrewMember, deploymentDate: string): void {
    this.donationHistory.set(c.characterId, deploymentDate);

    const userId = this.auth.currentSession?.userId;
    if (!userId || !this.auth.setCrewDonationHistory) return;

    // Pass the character's design (friendly) name, the donor-assigned custom
    // name (if any), and our own player name so the server can correlate this
    // donation to "X has borrowed character Y from Z" lines in
    // fleet_chat_messages — PSS chat refers to renamed crew by the custom name.
    const characterName = this.characterDesigns.get(c.designId)?.name || null;
    const customName    = (c.customName || '').trim() || null;
    const userName      = this.auth.currentSession?.name || null;
    this.auth.setCrewDonationHistory(userId, c.characterId, deploymentDate, characterName, userName, customName).subscribe();
  }

  private effectiveDonationDate(c: PssCrewMember): string {
    const historyDate = this.donationHistory.get(c.characterId);
    if (historyDate) return historyDate;
    return c.deploymentDate || '';
  }

  lastDonationLabel(c: PssCrewMember): string {
    const donationDate = this.effectiveDonationDate(c);
    if (!this.isRealDeploymentDate(donationDate)) return 'Never';
    const dateISO = donationDate.endsWith('Z') ? donationDate : donationDate + 'Z';
    const parsed = new Date(dateISO);
    if (Number.isNaN(parsed.getTime())) return 'Invalid';
    return dateISO.replace('T', ' ').replace('Z', '').replace('.000', '');
  }

  donationTimeLeft(c: PssCrewMember): string {
    if (!c.deploymentDate) return '';
    const expiry = new Date(c.deploymentDate + 'Z').getTime() + 24 * 3600 * 1000;
    const diff = expiry - Date.now();
    if (diff <= 0) return 'Expired';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  }

  canExtend(c: PssCrewMember): boolean {
    if (!c.deploymentDate) return false;
    return Date.now() - new Date(c.deploymentDate + 'Z').getTime() >= 3600000;
  }

  private loadCrewFilters(): void {
    try {
      const raw = localStorage.getItem(CrewTabComponent.STORAGE_KEY);
      if (!raw) {
        this.crewSortBy = 'donationDate';
        this.crewSortAsc = false;
        return;
      }
      const saved = JSON.parse(raw) as any;
      if (saved.crewSearch) this.crewSearch = saved.crewSearch;
      if (saved.crewAbilityFilter) this.crewAbilityFilter = saved.crewAbilityFilter;
      if (saved.crewCollectionFilter) this.crewCollectionFilter = saved.crewCollectionFilter;
      if (saved.crewRarityFilter) this.crewRarityFilter = saved.crewRarityFilter;
      if (saved.crewSortBy) this.crewSortBy = saved.crewSortBy;
      if (typeof saved.crewSortAsc === 'boolean') this.crewSortAsc = saved.crewSortAsc;
      if (typeof saved.showDonated === 'boolean') this.showDonated = saved.showDonated;
      if (typeof saved.showLift === 'boolean') this.showLift = saved.showLift;
      if (typeof saved.showDeployed === 'boolean') this.showDeployed = saved.showDeployed;

      // Enforce default available-to-donate behavior: show recently donated first
      if (this.crewSortBy !== 'donationDate') {
        this.crewSortBy = 'donationDate';
        this.crewSortAsc = false;
      }
    } catch {
      this.crewSortBy = 'donationDate';
      this.crewSortAsc = false;
    }
  }

  private saveCrewFilters(): void {
    const payload = {
      crewSearch: this.crewSearch,
      crewAbilityFilter: this.crewAbilityFilter,
      crewCollectionFilter: this.crewCollectionFilter,
      crewRarityFilter: this.crewRarityFilter,
      crewSortBy: this.crewSortBy,
      crewSortAsc: this.crewSortAsc,
      showDonated: this.showDonated,
      showLift: this.showLift,
      showDeployed: this.showDeployed,
    };
    try {
      localStorage.setItem(CrewTabComponent.STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /**/ }
  }

  extendCooldownLeft(c: PssCrewMember): string {
    if (!c.deploymentDate) return '';
    const remaining = 3600000 - (Date.now() - new Date(c.deploymentDate + 'Z').getTime());
    if (remaining <= 0) return '';
    const m = Math.ceil(remaining / 60000);
    return `Extend in ${m}m`;
  }

  donate(c: PssCrewMember): void {
    if (this.restrictedMode) return;
    this.crewBusy.set(c.characterId, true);
    this.crewError.delete(c.characterId);
    this.auth.donateCrew(c.characterId).subscribe({
      next: () => {
        c.donated = true;
        const now = new Date().toISOString().replace('Z', '');
        c.deploymentDate = now;
        this.saveDonationHistory(c, now);
        this.crewBusy.delete(c.characterId);
      },
      error: (err) => {
        this.crewError.set(c.characterId, err?.error?.error || 'Donate failed');
        this.crewBusy.delete(c.characterId);
      }
    });
  }

  extend(c: PssCrewMember): void {
    if (this.restrictedMode) return;
    this.crewBusy.set(c.characterId, true);
    this.crewError.delete(c.characterId);
    this.auth.extendDonation(c.characterId).subscribe({
      next: () => {
        const now = new Date().toISOString().replace('Z', '');
        c.deploymentDate = now;
        this.saveDonationHistory(c, now);
        this.crewBusy.delete(c.characterId);
      },
      error: (err) => {
        console.error('[extend] error for characterId:', c.characterId, err);
        const detail = err?.error?.error ?? err?.message ?? 'Extend failed';
        const missing = err?.error?.missing as string[] | undefined;
        const msg = missing?.length
          ? `${detail} (missing: ${missing.join(', ')})`
          : detail;
        this.crewError.set(c.characterId, msg);
        this.crewBusy.delete(c.characterId);
      }
    });
  }
}
